import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertCauseMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertCauseTRPCInput = {
  ParentIssueId: string;
  Title: string;
  Description?: string | null;
  Significance?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertCauseMutation => {
  return {
    insert_cause: {
      __typename: 'cause_mutation_response',
      returning: [{ __typename: 'cause', Id: trpcData.Id }],
    },
  };
};

export const useInsertCauseTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.cause.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.cause.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.cause.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.cause.getByParentIssueId.queryKey(),
        });
      },
    }),
  });

  return {
    insertCause: async (
      variables: InsertCauseTRPCInput
    ): Promise<InsertCauseMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
